import { type FC, useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Search,
  Bell,
  Moon,
  Sun,
  Smartphone,
  Users,
  CalendarClock,
  LogOut,
  Settings,
  User,
  Command,
  Bot,
  Activity,
} from 'lucide-react';
import { cn } from '../../lib/utils.js';
import { useUIStore } from '../../store/ui-store.js';
import { useAuthStore } from '../../store/auth-store.js';
import { useNotificationStore } from '../../store/notification-store.js';
import { useDeviceStore } from '../../store/device-store.js';
import { useWebSocket } from '../../hooks/useWebSocket.js';
import { useAiStatus } from '../../hooks/use-ai-status.js';
import { Avatar, AvatarFallback } from '../ui/avatar.js';
import { StatusDot } from '../ui/status-dot.js';
import { Badge } from '../ui/badge.js';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuLabel,
} from '../ui/dropdown-menu.js';

const navLabels: Record<string, string> = {
  '/': 'Dashboard',
  '/creators': 'Creators',
  '/devices': 'Devices',
  '/content': 'Content Library',
  '/scheduler': 'Scheduling',
  '/automation': 'Automation',
  '/analytics': 'Analytics',
  '/ai': 'AI Intelligence',
  '/storage': 'Storage',
  '/notifications': 'Notifications',
  '/activity': 'Activity Logs',
  '/integrity': 'Content Integrity',
  '/health': 'System Health',
  '/settings': 'Settings',
};

interface GlobalHeaderProps {
  currentPath: string;
}

export const GlobalHeader: FC<GlobalHeaderProps> = ({ currentPath }) => {
  const navigate = useNavigate();
  const { data: aiStatus } = useAiStatus();
  const { theme, toggleTheme, setCommandPaletteOpen } = useUIStore();
  const { user, logout } = useAuthStore();
  const { connected } = useWebSocket();
  const { notifications, unreadCount, markAllRead } = useNotificationStore();
  const devices = useDeviceStore((s) => s.devices);
  const [showNotifications, setShowNotifications] = useState(false);
  const [searchValue, setSearchValue] = useState('');
  const searchRef = useRef<HTMLInputElement>(null);

  const deviceCount = devices?.devices.length ?? 0;
  const onlineDevices = devices?.devices.filter((d) => d.connectionStatus === 'CONNECTED').length ?? 0;

  const pageTitle = navLabels[currentPath] ?? 'LaxmiAI';

  // Command palette keyboard shortcut
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setCommandPaletteOpen(true);
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [setCommandPaletteOpen]);

  const handleLogout = () => {
    logout();
    navigate('/login', { replace: true });
  };

  const handleSearchSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (searchValue.trim().length > 0) {
      navigate('/search?q=' + encodeURIComponent(searchValue));
    }
  };

  return (
    <header className="flex h-14 items-center justify-between border-b border-slate-800 bg-slate-900/80 px-4 backdrop-blur-sm">
      {/* Left: Page title */}
      <div className="flex items-center gap-3">
        <h1 className="text-base font-semibold text-slate-100">{pageTitle}</h1>
      </div>

      {/* Center: Global search */}
      <form onSubmit={handleSearchSubmit} className="hidden md:block">
        <div className="relative">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-500" />
          <input
            ref={searchRef}
            value={searchValue}
            onChange={(e) => setSearchValue(e.target.value)}
            placeholder="Search anything... (⌘K)"
            className="h-9 w-80 rounded-lg border border-slate-800 bg-slate-800/50 pl-10 pr-10 text-sm text-slate-300 placeholder:text-slate-600 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
          />
          <kbd className="pointer-events-none absolute right-3 top-1/2 hidden -translate-y-1/2 items-center gap-0.5 rounded border border-slate-700 bg-slate-800 px-1.5 py-0.5 text-[10px] font-medium text-slate-500 sm:flex">
            <Command className="h-2.5 w-2.5" />K
          </kbd>
        </div>
      </form>

      {/* Right: Status + Actions */}
      <div className="flex items-center gap-2">
        {/* AI status: what GET /api/v1/ai/status says, never a static label */}
        <button
          type="button"
          onClick={() => navigate('/ai')}
          title={aiStatus === undefined ? 'AI status' : aiStatus.enabled ? `AI on: ${aiStatus.model ?? aiStatus.provider ?? ''}` : 'AI is off on this server'}
          className="hidden items-center gap-1.5 rounded-lg bg-slate-800/50 px-2.5 py-1.5 hover:bg-slate-800 md:flex"
        >
          <Bot className={cn('h-3.5 w-3.5', aiStatus?.enabled === true ? 'text-blue-400' : 'text-slate-600')} />
          <span className="text-[11px] font-medium text-slate-400">
            {aiStatus === undefined ? 'AI' : aiStatus.enabled ? 'AI ready' : 'AI off'}
          </span>
        </button>

        {/* Connected devices */}
        <div className="hidden items-center gap-1.5 rounded-lg bg-slate-800/50 px-2.5 py-1.5 md:flex">
          <Smartphone className="h-3.5 w-3.5 text-emerald-400" />
          <span className="text-[11px] font-medium text-slate-400">
            {onlineDevices}/{deviceCount}
          </span>
        </div>

        {/* Scheduled posts */}
        <div className="hidden items-center gap-1.5 rounded-lg bg-slate-800/50 px-2.5 py-1.5 lg:flex">
          <CalendarClock className="h-3.5 w-3.5 text-amber-400" />
          <span className="text-[11px] font-medium text-slate-400">12</span>
        </div>

        {/* WebSocket status */}
        <StatusDot status={connected ? 'online' : 'offline'} pulse={connected} className="mx-1" />

        {/* Theme toggle */}
        <button
          onClick={toggleTheme}
          className="flex h-8 w-8 items-center justify-center rounded-lg text-slate-500 hover:bg-slate-800 hover:text-slate-300"
        >
          {theme === 'dark' ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
        </button>

        {/* Notifications */}
        <DropdownMenu open={showNotifications} onOpenChange={setShowNotifications}>
          <DropdownMenuTrigger asChild>
            <button className="relative flex h-8 w-8 items-center justify-center rounded-lg text-slate-500 hover:bg-slate-800 hover:text-slate-300">
              <Bell className="h-4 w-4" />
              {unreadCount > 0 && (
                <span className="absolute right-1.5 top-1.5 flex h-4 min-w-[14px] items-center justify-center rounded-full bg-red-500 px-1 text-[9px] font-bold text-white">
                  {unreadCount}
                </span>
              )}
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-80">
            <DropdownMenuLabel className="flex items-center justify-between">
              <span>Notifications</span>
              {unreadCount > 0 && (
                <button onClick={markAllRead} className="text-[10px] text-blue-400 hover:text-blue-300">
                  Mark all read
                </button>
              )}
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            {notifications.length === 0 && (
              <DropdownMenuItem onClick={() => navigate('/notifications')} className="text-xs text-slate-400">
                No new alerts. Open the Notifications page for recent events.
              </DropdownMenuItem>
            )}
            <div className="max-h-72 overflow-y-auto">
              {notifications.slice(0, 8).map((n) => (
                <div
                  key={n.id}
                  className={cn(
                    'flex gap-3 px-2 py-2.5 transition-colors hover:bg-slate-800',
                    !n.read && 'bg-blue-900/10',
                  )}
                >
                  <div className="mt-0.5">
                    <div
                      className={cn(
                        'flex h-6 w-6 items-center justify-center rounded-full',
                        n.severity === 'error' && 'bg-red-900/50 text-red-400',
                        n.severity === 'warning' && 'bg-amber-900/50 text-amber-400',
                        n.severity === 'success' && 'bg-emerald-900/50 text-emerald-400',
                        (n.severity === 'info' || n.severity === undefined) && 'bg-blue-900/50 text-blue-400',
                      )}
                    >
                      <Activity className="h-3 w-3" />
                    </div>
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-xs font-medium text-slate-200">{n.title}</p>
                    {n.description !== undefined && (
                      <p className="mt-0.5 text-[11px] text-slate-500">{n.description}</p>
                    )}
                    <p className="mt-0.5 text-[10px] text-slate-600">
                      {new Date(n.timestamp).toLocaleTimeString()}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </DropdownMenuContent>
        </DropdownMenu>

        {/* User menu */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button className="flex items-center gap-2 rounded-lg p-1 hover:bg-slate-800">
              <Avatar className="h-7 w-7">
                <AvatarFallback>{(user?.name ?? 'U').charAt(0).toUpperCase()}</AvatarFallback>
              </Avatar>
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-48">
            <DropdownMenuLabel>{user?.name ?? 'User'}</DropdownMenuLabel>
            <DropdownMenuLabel className="text-xs font-normal text-slate-500 -mt-1">{user?.email}</DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={() => navigate('/settings')}>
              <Settings className="mr-2 h-4 w-4" />
              Settings
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={handleLogout}>
              <LogOut className="mr-2 h-4 w-4" />
              Sign out
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </header>
  );
};
